/**
 * The browser half of the push: one `EventSource`, and what it does to the
 * TanStack Query cache.
 *
 * ===========================================================================
 * WHAT THIS REPLACES
 *
 * era-control pushes `session_started` / `session_ended` / `recording_ready` /
 * `transcript_ready` to the BFF, signed, and the BFF applies each one to SQLite
 * in about a millisecond. Until now the browser found out by asking again —
 * `useSessionDetail` every 15 s, `useHistory` every 8 s — so a transcript that
 * arrived by push could take fifteen seconds to appear on a screen that was
 * open and watching for it. FINDINGS.md #33(c).
 *
 * `GET /api/events` carries that delivery the last hop. Measured end to end,
 * through nginx and TLS, from the moment era-control's POST leaves to the
 * moment the frame is in this file: **~15 ms**.
 *
 * ===========================================================================
 * ONE CONNECTION PER TAB, REFCOUNTED
 *
 * Every hook that wants live updates calls `useLiveUpdates()`; the first one
 * opens the connection and the last one to unmount closes it. Not one per
 * hook, and definitely not one per session: browsers cap concurrent HTTP/1.1
 * connections at six per origin, and this app also streams audio bytes through
 * the same origin, so a second or third stream is taken directly out of the
 * player's budget.
 *
 * That is also why the client does NOT use the server's `?session=` filter,
 * which exists and works. Filtering server-side would mean a connection whose
 * scope changes as you navigate — tear down and re-open on every route change,
 * losing `Last-Event-ID` continuity each time — to save a few dozen bytes on
 * frames the projection below already ignores. The filter is there for a
 * consumer that has to have one (see the scoping note in
 * `apps/bff/src/routes/events.ts`); this app does not.
 *
 * ===========================================================================
 * THE POLLS DID NOT GO AWAY, AND MUST NOT
 *
 * `useHistory` and `useSessionDetail` still poll — at 60 s and 90 s instead of
 * 8 s and 15 s, and only while there is something unfinished to poll ABOUT.
 * FINDINGS #33 said the 15-second poll "goes away entirely rather than being
 * made quieter". That was written before this file existed and it is wrong,
 * for three reasons that are all real:
 *
 *   1. **SSE is blockable.** A corporate proxy that buffers responses turns
 *      this stream into a socket that connects and never speaks. So does a
 *      browser extension, and so does an nginx `location` block that somebody
 *      later splits off from `/api/` and forgets `proxy_buffering off` on. In
 *      every one of those cases the connection LOOKS healthy. A client with no
 *      poll would freeze with no error anywhere.
 *   2. **The bus is in-process and its replay ring is finite.** A BFF restart
 *      mid-call, or a reconnect after the ring has rolled, produces a gap. The
 *      server is honest about it — it sends `resync` — but a `resync` can only
 *      be delivered on a connection that came back. If it did not, the poll is
 *      what notices.
 *   3. **Not every session is covered.** The webhook is frozen with the room
 *      at creation, so a room created before webhooks existed, or from a
 *      laptop that could not receive them, will never produce an event. They
 *      are one subset of the unfinished rows `refreshing > 0` counts, and the
 *      poll is the only thing that will ever finish that subset.
 *
 * A reference client should DEGRADE, not freeze. The cadences below are picked
 * so that when the stream works the poll is nearly free, and when it does not
 * the app is merely as slow as it was last week.
 */

import * as React from 'react'
import type { HistoryResponse, SessionDetailView, SessionState } from '@era/shared'
import { keys, queryClient } from './query'

/* -------------------------------------------------------------------------- */
/* the wire type                                                               */
/* -------------------------------------------------------------------------- */

/**
 * One frame's `data`, mirroring `LiveEvent` in `apps/bff/src/services/bus.ts`.
 *
 * Declared here rather than generated into `@era/shared` because this is the
 * BFF's own contract with its own browser, not era-control's — the generated
 * package is a projection of `/openapi.json` and nothing that is not in that
 * document belongs in it. `kind` is deliberately a widened `string` and not a
 * union: see the note on unknown kinds in `project()` below.
 */
interface LiveEvent {
  kind: string
  session_id: string
  state: SessionState | null
  joinable: boolean | null
  seq: number
  id: string
  at: string
}

/* -------------------------------------------------------------------------- */
/* the projection onto the query cache                                         */
/* -------------------------------------------------------------------------- */

/**
 * What an event does to the cache. **This is the pattern worth copying.**
 *
 * Two steps, and they answer two different questions.
 *
 * ### 1. `setQueryData` — for what the event itself carries
 *
 * The event carries `state` and, on a finalized controller, first-class
 * `joinable`. Both came out of the same delivery and transaction that wrote the
 * row. Patching them straight into the cache updates the badges with **zero
 * requests**. `joinable` is never derived from state — that was the exact bug
 * FINDINGS #19 records — and a rolling null simply leaves it unchanged.
 *
 * ### 2. `invalidateQueries` — for everything the event does not carry
 *
 * Which is most of it: transcript/activity documents, full pipeline status and
 * the participant roster. A current `transcript_ready` has already committed
 * its product ids, transcript and metrics to SQLite, but invalidation still
 * makes the active query read that committed projection. Invalidation is not a
 * refetch storm, and the reason is
 * worth knowing rather than trusting: `invalidateQueries` marks matching
 * queries stale and refetches only the **active** ones — the queries some
 * mounted component is currently observing. A cached session nobody is looking
 * at is marked and left alone until something renders it. So an event for a
 * session that is not on screen costs one map lookup, not a request.
 *
 * `exact: true` on both, and that is load-bearing. Query keys here are
 * hierarchical — `['sessions']`, `['sessions', id]`, `['sessions', id,
 * 'transcript']` — and TanStack matches by PREFIX. Invalidating `['sessions']`
 * without `exact` would also invalidate every transcript document, each of
 * which is `staleTime: Infinity` precisely because it is immutable and
 * expensive. That would turn one webhook into a re-download of every open
 * transcript.
 *
 * ### What is deliberately not done
 *
 * No `refetchQueries`, and no `queryClient.setQueryData` of a document body.
 * The event is a pointer that says "re-read this"; the read routes stay the
 * single source of truth, and they keep their error handling, their 409
 * "not ready yet" semantics and their caching. A stream that also delivered
 * content would be a second source of truth to keep in step with the first.
 */
function project(event: LiveEvent): void {
  const { session_id: sessionId, state } = event
  const joinable = typeof event.joinable === 'boolean' ? event.joinable : null

  // Live recording changes are app-local and their event intentionally carries
  // no policy body. Re-read every ACTIVE room-resolution query; normally that
  // is exactly the call in this tab. The write committed before publication.
  if (event.kind === 'recording_changed') {
    void queryClient.invalidateQueries({ queryKey: keys.rooms })
  }
  if (event.kind === 'composite_ready') {
    void queryClient.invalidateQueries({
      queryKey: [...keys.session(sessionId), 'video'],
      exact: true,
    })
  }

  /* -- 1. the cheap, immediate patch --------------------------------------- */

  if (state || joinable !== null) {
    queryClient.setQueryData<HistoryResponse>(keys.sessions, (previous) => {
      if (!previous) return previous
      const index = previous.rooms.findIndex((room) => room.session_id === sessionId)
      if (index < 0) return previous
      // A new array and a new row: TanStack compares by reference to decide
      // whether to re-render, so mutating in place would update nothing.
      const rooms = previous.rooms.slice()
      rooms[index] = {
        ...rooms[index]!,
        ...(state ? { session_state: state } : {}),
        ...(joinable !== null ? { status: joinable ? ('open' as const) : ('closed' as const) } : {}),
      }
      return { ...previous, rooms }
    })

    queryClient.setQueryData<SessionDetailView>(keys.session(sessionId), (previous) => {
      if (!previous) return previous
      return {
        ...previous,
        ...(state ? { state } : {}),
        ...(joinable !== null ? { joinable } : {}),
      }
    })
  }

  /* -- 2. reconcile everything else ---------------------------------------- */

  // An unknown `kind` lands here too, and that is the point of the widened
  // type: a future event this bundle has never heard of still means "session
  // X moved", and re-reading is always the safe response to that.
  void queryClient.invalidateQueries({ queryKey: keys.sessions, exact: true })
  void queryClient.invalidateQueries({ queryKey: keys.session(sessionId), exact: true })
  void queryClient.invalidateQueries({
    queryKey: [...keys.session(sessionId), 'activity'],
    exact: true,
  })
}

/**
 * The server admitting it cannot prove we missed nothing.
 *
 * Sent when a reconnect arrives with a `Last-Event-ID` from a previous boot of
 * the BFF, or one that has fallen out of its replay ring. The honest response
 * is to distrust the whole cache: invalidate everything and let the active
 * screens re-read. It is one or two requests, once, and it is the alternative
 * to a cache that is quietly missing an update forever.
 */
function resync(): void {
  void queryClient.invalidateQueries()
}

/* -------------------------------------------------------------------------- */
/* the connection                                                              */
/* -------------------------------------------------------------------------- */

/**
 * How long the connection lingers after the last subscriber unmounts.
 *
 * Navigating from `/history` to `/session/$id` unmounts one hook and mounts
 * another in the same tick. Without a grace period that is a teardown and a
 * fresh TLS handshake on every route change — and, worse, a lost
 * `Last-Event-ID` continuity each time, so anything published during the
 * changeover falls into the reconnect gap instead of being replayed.
 */
const LINGER_MS = 5_000

/**
 * How long to wait before re-opening a stream the browser gave up on.
 *
 * `EventSource` reconnects by itself ONLY after a transport-level drop. If the
 * server answers with a non-2xx, or with the wrong `Content-Type`, the spec
 * says the connection is "failed" — `readyState` goes to `CLOSED` and the
 * browser never tries again. That is not exotic: it is what a 502 during a BFF
 * deploy looks like, and what the `MAX_STREAMS` 503 looks like.
 *
 * Without a manual re-open, a single unlucky second permanently demotes that
 * tab to polling, with nothing on screen to say so. Ten seconds is slow enough
 * that a server refusing connections is not hammered, and fast enough that a
 * deploy costs one backstop poll.
 */
const REOPEN_MS = 10_000

let source: EventSource | null = null
let refcount = 0
let lingerTimer: ReturnType<typeof setTimeout> | null = null
let reopenTimer: ReturnType<typeof setTimeout> | null = null
/**
 * Set when we are re-opening after a hard failure rather than letting the
 * browser reconnect.
 *
 * It matters because a NEW `EventSource` sends no `Last-Event-ID` — the cursor
 * belonged to the object we just threw away — so the server has no way to know
 * we have a gap and will not send `resync`. We know, so we do it ourselves on
 * the next successful open. Skipping this is how a reference client ends up
 * with a cache that is quietly missing one update for the rest of the session.
 */
let resyncOnOpen = false

/** `useSyncExternalStore` plumbing, so a component can render the state. */
let connected = false
const statusListeners = new Set<() => void>()

function setConnected(next: boolean): void {
  if (connected === next) return
  connected = next
  for (const listener of statusListeners) listener()
}

function open(): void {
  if (source) return

  // Relative URL, same-origin, exactly like `lib/api.ts` — Vite proxies `/api`
  // in development and nginx proxies it in production, so there is one shape
  // exercised in both. `withCredentials` is deliberately absent: this app
  // sends no credential to its own server, and asking for one here would put
  // the stream on a different CORS footing from every other request.
  const es = new EventSource('/api/events')
  source = es

  es.onopen = () => {
    setConnected(true)
    if (resyncOnOpen) {
      resyncOnOpen = false
      resync()
    }
  }

  es.onerror = () => {
    setConnected(false)

    // NOT an error to surface, and — in the common case — not a reason to
    // close anything. `onerror` fires on every ordinary disconnect: a laptop
    // lid, a tunnel, a restarted BFF. In all of those `readyState` is
    // `CONNECTING`, the browser is already retrying on the `retry:` interval
    // the server sent, and it will carry `Last-Event-ID` so the gap is
    // replayed. Calling `close()` there is the classic mistake — it turns a
    // self-healing stream into a one-shot one.
    if (es.readyState !== EventSource.CLOSED) return

    // `CLOSED` is the other case, and it is the one people miss: the browser
    // has given up for good, because the response was not a 2xx
    // `text/event-stream`. Nothing will ever reconnect this object. Throw it
    // away and open a new one on a slow timer — and remember that the new one
    // starts with no cursor, so the cache has to be distrusted once.
    if (source === es) source = null
    es.close()
    resyncOnOpen = true
    scheduleReopen()
  }

  /*
   * Two channels, both stable. See the framing note in
   * `apps/bff/src/routes/events.ts` for why the webhook's own name is NOT the
   * channel name: `EventSource` has no catch-all, so a frame whose name has no
   * listener is dropped silently, and this bundle would go blind the day the
   * platform adds an event it predates.
   */
  es.addEventListener('session_event', (message) => {
    // Anything that reaches here has been framed by our own server, but a
    // malformed frame must not take the stream down with it.
    let event: LiveEvent
    try {
      event = JSON.parse((message as MessageEvent<string>).data) as LiveEvent
    } catch {
      return
    }
    if (typeof event?.session_id !== 'string') return
    project(event)
  })

  es.addEventListener('resync', () => {
    resync()
  })
}

function scheduleReopen(): void {
  if (reopenTimer !== null) return
  reopenTimer = setTimeout(() => {
    reopenTimer = null
    // Only if something still wants it. A tab that navigated away in the
    // meantime must not be re-connected by a timer nobody is waiting on.
    if (refcount > 0) open()
  }, REOPEN_MS)
}

function close(): void {
  source?.close()
  source = null
  if (reopenTimer !== null) {
    clearTimeout(reopenTimer)
    reopenTimer = null
  }
  setConnected(false)
}

function acquire(): () => void {
  refcount += 1
  if (lingerTimer !== null) {
    clearTimeout(lingerTimer)
    lingerTimer = null
  }
  open()

  let released = false
  return () => {
    if (released) return
    released = true
    refcount -= 1
    if (refcount > 0) return
    lingerTimer = setTimeout(() => {
      lingerTimer = null
      if (refcount === 0) close()
    }, LINGER_MS)
  }
}

/* -------------------------------------------------------------------------- */
/* the hook                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Open the live stream for as long as this component is mounted, and report
 * whether it is currently up.
 *
 * The return value is used for exactly one thing and it is not cosmetic: it
 * chooses the polling cadence. Stream up → the poll is a distant backstop;
 * stream down → the poll goes back to being the mechanism. See `useHistory`
 * and `useSessionDetail`.
 *
 * There is no `sessionId` argument. The connection is shared, the frames are a
 * session id and a state word, and `project()` addresses the cache by the id
 * in the frame — so a component does not have to say what it is interested in,
 * and two components on screen at once still share one socket.
 */
export function useLiveUpdates(): boolean {
  React.useEffect(() => acquire(), [])

  return React.useSyncExternalStore(
    (onChange) => {
      statusListeners.add(onChange)
      return () => statusListeners.delete(onChange)
    },
    () => connected,
    // Server snapshot: this app does not server-render, but the third argument
    // is what stops `useSyncExternalStore` throwing if it ever does.
    () => false,
  )
}
